import { FiLink2 } from 'react-icons/fi';

export const sophisticatedConnectorsData = {
  courseId: 'sophisticated-connectors',
  courseType: 'vocabulary',
  title: 'Sophisticated Connectors',
  icon: FiLink2,
  overview: {
    impact: 'Master Band 7+ cohesive devices that demonstrate advanced language proficiency to IELTS examiners',
    focus: 'High-register connectors (notwithstanding, albeit, insofar as, hitherto, thereby, whereby), appropriate usage, and avoiding over-formality',
    uses: 'Writing Task 2 sophistication, Reading comprehension of formal texts',
    duration: '20-25 minutes'
  },

  sections: [
    {
      title: 'Beyond Basic Connectors',
      content: `<p>At Band 6, candidates use common connectors like "however" and "therefore". To reach <strong>Band 7–9</strong>, you need a wider range of cohesive devices — including connectors that most test-takers never use.</p>
<p>Sophisticated connectors signal to the examiner that you have a <strong>wide vocabulary range</strong> and can manage <strong>complex arguments</strong>. However, they must be used correctly and sparingly — misuse can actually lower your score.</p>
<ul>
  <li><strong>Key principle:</strong> Use 2–3 sophisticated connectors per essay, blended naturally with simpler ones</li>
  <li><strong>Risk:</strong> Overloading an essay with rare connectors sounds unnatural and forced</li>
  <li><strong>Reward:</strong> Even one well-placed sophisticated connector can lift the examiner's impression</li>
</ul>`,
      points: [
        'Band 7+ descriptor: "uses a range of cohesive devices appropriately"',
        'The goal is not to replace all basic connectors — it is to supplement them strategically',
        'Sophisticated connectors are also common in IELTS Reading passages, especially academic texts'
      ]
    },
    {
      title: 'High-Band Connectors Reference',
      content: `<p>Study these connectors, their precise meanings, and the contexts where they fit naturally.</p>
<table>
  <thead><tr><th>Connector</th><th>Meaning</th><th>Example</th><th>Formality</th></tr></thead>
  <tbody>
    <tr><td>notwithstanding</td><td>in spite of; nevertheless</td><td>The project continued, <em>notwithstanding</em> the budget cuts.</td><td>Very formal</td></tr>
    <tr><td>albeit</td><td>although; even if (concession)</td><td>The results were positive, <em>albeit</em> slightly lower than expected.</td><td>Formal</td></tr>
    <tr><td>insofar as</td><td>to the extent that</td><td>The policy is effective <em>insofar as</em> it reduces short-term pollution.</td><td>Very formal</td></tr>
    <tr><td>hitherto</td><td>until now; up to this point</td><td><em>Hitherto</em>, no study has examined this link in developing countries.</td><td>Very formal</td></tr>
    <tr><td>thereby</td><td>as a result of that; by that means</td><td>Costs were cut by 30%, <em>thereby</em> improving overall profitability.</td><td>Formal</td></tr>
    <tr><td>whereby</td><td>by which; through which</td><td>A system was introduced <em>whereby</em> students could track their progress online.</td><td>Formal</td></tr>
    <tr><td>inasmuch as</td><td>since; because (to the degree that)</td><td>The study is significant <em>inasmuch as</em> it challenges previous assumptions.</td><td>Very formal</td></tr>
    <tr><td>conversely</td><td>in an opposite way</td><td>Urban areas saw growth; <em>conversely</em>, rural populations declined.</td><td>Formal</td></tr>
    <tr><td>in light of</td><td>considering; because of</td><td><em>In light of</em> recent evidence, the theory has been revised.</td><td>Formal</td></tr>
  </tbody>
</table>`,
      points: [
        '"Albeit" is always followed by an adjective, noun, or short phrase — never a full clause',
        '"Thereby" is followed by a gerund (-ing form): "thereby reducing…", "thereby creating…"',
        '"Whereby" introduces a relative clause explaining a system or method'
      ]
    },
    {
      title: 'When to Use Sophisticated Connectors',
      content: `<p>Placement and frequency matter as much as correctness. Follow these guidelines:</p>
<ul>
  <li><strong>Use in Writing Task 2 body paragraphs</strong> — especially when making a concession or showing cause-effect</li>
  <li><strong>Limit to 2–3 per essay</strong> — one in each body paragraph is sufficient</li>
  <li><strong>Mix with simple connectors:</strong> "Moreover, … <em>Albeit</em> …" sounds natural; "Notwithstanding, … Albeit, … Inasmuch as …" sounds forced</li>
  <li><strong>Use in academic Reading to recognise arguments:</strong> when you see "notwithstanding" or "albeit" in a passage, the author is making a concession</li>
</ul>
<p><strong>Good example:</strong> <em>Technology has transformed education significantly. Online platforms allow students to access courses from top universities, <strong>thereby</strong> democratising knowledge. However, not all students benefit equally, particularly those in areas with limited internet, <strong>albeit</strong> efforts are being made to bridge this gap.</em></p>`,
      points: [
        'Use sophisticated connectors where they add genuine clarity, not just to impress',
        'Speaking: avoid these connectors in the IELTS Speaking test — they sound rehearsed in conversation',
        'The best Band 9 essays use sophisticated connectors so naturally that the reader barely notices them'
      ]
    },
    {
      title: 'Common Misuse Pitfalls',
      content: `<p>Even advanced learners make these mistakes with sophisticated connectors. Avoid them to protect your score.</p>
<table>
  <thead><tr><th>Mistake</th><th>Wrong</th><th>Correct</th></tr></thead>
  <tbody>
    <tr><td>"Albeit" + full clause</td><td>Albeit the weather was bad, we went out.</td><td>We went out, <em>albeit</em> reluctantly. / <em>Although</em> the weather was bad, we went out.</td></tr>
    <tr><td>"Thereby" + base verb</td><td>…thereby reduce costs.</td><td>…thereby <em>reducing</em> costs. ("Thereby" needs -ing)</td></tr>
    <tr><td>"Whereby" as "where"</td><td>The city whereby I grew up…</td><td>A system <em>whereby</em> citizens can vote online… / The city <em>where</em> I grew up…</td></tr>
    <tr><td>Overuse in one paragraph</td><td>Notwithstanding this, … albeit small, … thereby creating … hitherto unknown…</td><td>Use maximum one sophisticated connector per paragraph</td></tr>
    <tr><td>"Hitherto" in informal context</td><td>I hitherto didn't know about this restaurant.</td><td><em>Hitherto</em>, no comprehensive study had addressed this issue.</td></tr>
  </tbody>
</table>`,
      points: [
        'If you are not 100% sure of the grammar pattern, use the simpler alternative instead',
        'Misusing a sophisticated connector is worse than not using one — it signals overreach',
        'Practice each connector in 5–10 sentences before using it in an exam essay'
      ]
    }
  ],

  exercises: [
    {
      title: 'Match Connector to Meaning',
      type: 'select',
      instructions: 'Choose the correct meaning for each sophisticated connector.',
      questions: [
        {
          text: 'What does "notwithstanding" mean?',
          options: ['as a result', 'in spite of', 'in addition to', 'by means of'],
          answer: 'in spite of',
          explanation: '"Notwithstanding" means despite or in spite of — it introduces a point that contrasts with what was said before.'
        },
        {
          text: 'What does "albeit" mean?',
          options: ['therefore', 'although / even if', 'because of', 'by which'],
          answer: 'although / even if',
          explanation: '"Albeit" means although or even if, used to introduce a concession. It is always followed by a short phrase, not a full clause.'
        },
        {
          text: 'What does "thereby" mean?',
          options: ['in contrast', 'before that', 'by that means / as a result', 'to the extent that'],
          answer: 'by that means / as a result',
          explanation: '"Thereby" means "by doing that" or "as a result of that". It is followed by a gerund (-ing form).'
        },
        {
          text: 'What does "hitherto" mean?',
          options: ['from now on', 'until now', 'on the other hand', 'in addition'],
          answer: 'until now',
          explanation: '"Hitherto" means until now or up to this point in time. It is used in formal academic contexts.'
        }
      ]
    },
    {
      title: 'Choose the Appropriate Connector',
      type: 'fill-in',
      instructions: 'Fill in the blank with the most appropriate sophisticated connector.',
      questions: [
        {
          text: 'The company introduced a mentoring programme _______ new employees could learn directly from senior staff.',
          answer: 'whereby',
          explanation: '"Whereby" means "by which" or "through which" — it introduces a system or method. The sentence describes a programme through which learning happens.'
        },
        {
          text: 'Automation has replaced many manual jobs, _______ increasing efficiency but reducing employment.',
          answer: 'thereby',
          explanation: '"Thereby" means "by that means" and is followed by a gerund (-ing). It shows the result of automation.'
        },
        {
          text: '_______, researchers had focused exclusively on adult populations; this study is the first to include children.',
          answer: 'Hitherto',
          explanation: '"Hitherto" means "until now". It sets up the contrast between past research focus and the new study.'
        },
        {
          text: 'The event was a success, _______ somewhat smaller than anticipated.',
          answer: 'albeit',
          explanation: '"Albeit" means "although" and is used to add a minor concession. It is followed by an adjective phrase, not a full clause.'
        }
      ]
    },
    {
      title: 'Identify Misused Connectors',
      type: 'select',
      instructions: 'Choose the sentence where the sophisticated connector is used CORRECTLY.',
      questions: [
        {
          text: 'Which sentence uses "albeit" correctly?',
          options: [
            'Albeit the rain was heavy, the match continued.',
            'The plan was approved, albeit with minor modifications.',
            'Albeit, the team performed well in the tournament.',
            'She albeit agreed with the proposal.'
          ],
          answer: 'The plan was approved, albeit with minor modifications.',
          explanation: '"Albeit" must be followed by a short phrase (adjective, noun, or prepositional phrase), not a full clause or used as a sentence adverb.'
        },
        {
          text: 'Which sentence uses "thereby" correctly?',
          options: [
            'The new law was passed, thereby reduce pollution levels.',
            'Thereby, the government introduced new measures.',
            'The factory was relocated, thereby reducing noise for nearby residents.',
            'She studied hard thereby she passed the exam.'
          ],
          answer: 'The factory was relocated, thereby reducing noise for nearby residents.',
          explanation: '"Thereby" must be followed by a gerund (-ing form) and cannot start a sentence or replace "therefore".'
        },
        {
          text: 'Which sentence uses "whereby" correctly?',
          options: [
            'The country whereby I was born is in West Africa.',
            'They created a system whereby all data was backed up automatically.',
            'Whereby the evidence was clear, the case was closed.',
            'She went to the office whereby she could work quietly.'
          ],
          answer: 'They created a system whereby all data was backed up automatically.',
          explanation: '"Whereby" means "by which" and describes a system, arrangement, or method — not a physical place.'
        },
        {
          text: 'Which sentence uses "notwithstanding" correctly?',
          options: [
            'Notwithstanding she was tired, she continued working.',
            'The project was completed on time, notwithstanding significant budget constraints.',
            'He notwithstanding refused to participate.',
            'Notwithstanding, the weather was excellent that day.'
          ],
          answer: 'The project was completed on time, notwithstanding significant budget constraints.',
          explanation: '"Notwithstanding" is typically followed by a noun phrase and means "in spite of". It is not used before a full clause with a subject and verb.'
        }
      ]
    },
    {
      title: 'Rewrite with Sophisticated Connectors',
      type: 'writing',
      instructions: 'Rewrite each sentence replacing the simple connector with a sophisticated alternative. Keep the meaning the same.',
      questions: [
        {
          text: 'Rewrite using "thereby": "The government invested in renewable energy, and as a result it reduced carbon emissions by 15%."',
          example: 'The government invested in renewable energy, thereby reducing carbon emissions by 15%.',
          tip: '"Thereby" replaces "and as a result" and must be followed by a gerund (-ing). It makes the sentence more concise and formal.'
        },
        {
          text: 'Rewrite using "albeit": "The recovery has been positive, although it has been slower than predicted."',
          example: 'The recovery has been positive, albeit slower than predicted.',
          tip: '"Albeit" replaces "although" for short concessive phrases. Drop the subject and verb — just use the adjective/phrase after "albeit".'
        },
        {
          text: 'Rewrite using "insofar as": "The study is useful because it highlights the impact of diet on cognitive function."',
          example: 'The study is useful insofar as it highlights the impact of diet on cognitive function.',
          tip: '"Insofar as" means "to the extent that" — it limits the scope of a claim, adding nuance. It works well in academic arguments.'
        }
      ]
    }
  ],

  tips: [
    'Limit yourself to 2–3 sophisticated connectors per Writing Task 2 essay — quality over quantity',
    'Learn the exact grammar pattern for each connector before using it (e.g., "albeit" + phrase, "thereby" + -ing)',
    'Read academic journals and opinion pieces to see sophisticated connectors used naturally',
    'Keep a "connector upgrade" list: pair each basic connector with its sophisticated alternative'
  ],
  dailyPractice: [
    'Study one new sophisticated connector per day — write 5 sentences using it correctly',
    'Read one editorial or academic abstract and underline any sophisticated connectors you find',
    'Take a paragraph you\'ve written and upgrade one connector to a sophisticated alternative',
    'Quiz yourself: cover the meanings and try to define each connector from memory'
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Sophisticated Connectors: Band 7+ cohesive devices including notwithstanding, albeit, insofar as, hitherto, thereby, whereby, inasmuch as. Covers precise meanings, grammar patterns, appropriate usage frequency, and common misuse pitfalls. Difficulty 4 (upper-intermediate/advanced). Exercises include meaning matching, contextual gap-fill, error identification, and sentence rewriting.'
};

export function getDataForDifficulty(difficulty = 4) {
  if (difficulty <= 2) {
    return {
      ...sophisticatedConnectorsData,
      title: sophisticatedConnectorsData.title + ' (Foundations)',
      overview: { ...sophisticatedConnectorsData.overview, duration: '15-20 minutes' },
      sections: sophisticatedConnectorsData.sections.slice(0, Math.max(2, Math.ceil(sophisticatedConnectorsData.sections.length / 2))),
      exercises: sophisticatedConnectorsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...sophisticatedConnectorsData,
      title: sophisticatedConnectorsData.title + ' (Advanced)',
      overview: { ...sophisticatedConnectorsData.overview, duration: '30-35 minutes' },
      sections: sophisticatedConnectorsData.sections,
      exercises: sophisticatedConnectorsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(sophisticatedConnectorsData.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return sophisticatedConnectorsData;
}
